import { elevate, localStorageBackedSession } from '@apptus/esales-api';
import { getClusterId, getMarket, getLocale, getTouchpoint, getVoyadoSession, hasCookieConsent } from './helpers/voyadoHelpers';
import { elevateFetchShim } from './helpers/elevateFetchShim';
import { getTicket, setTicket } from './helpers/handleTickets';
import handleFromUrl from '../../../../util/handleFromUrl';

// Use a constant for debug mode that can be easily toggled
const DEBUG = false;

// Initialize API with session based on cookie consent
// Use ephemeral session (random per request) if cookies declined, otherwise persisted session
const getApi = () => {
  const session = getVoyadoSession(localStorageBackedSession);
  return elevate({
    clusterId: getClusterId(),
    market: getMarket(),
    locale: getLocale(),
    touchpoint: getTouchpoint(),
    session: session
  });
};

// Helper function to get current resourceId
const getCurrentResourceId = (): string => {
  const pageMeta = (window as any).ShopifyAnalytics?.meta?.page;
  if (!pageMeta) return '';

  if (pageMeta.pageType === 'home') {
    return 'home';
  } else if (pageMeta.pageType === 'searchresults') {
    return `searchresults-${new URLSearchParams(window.location.search).get('q')}`;
  } else if (pageMeta.pageType === 'recommendations') {
    return pageMeta.resourceId;
  } else {
    return `${pageMeta.pageType}-${pageMeta.resourceId}`;
  }
};

// Helper function to get resourceId for autocomplete content clicks
const getAutocompleteResourceId = (target: HTMLElement): string | null => {
  const autocompleteContainer = target.closest('.search__results') ||
                               target.closest('[data-search-results]') ||
                               target.closest('.search__results--articles') ||
                               target.closest('.search__results--pages') ||
                               target.closest('.search__results--collections');

  if (autocompleteContainer) {
    let searchTerm = '';

    // Method 1: Look for search input
    const searchInput = document.querySelector('input[type="search"], input[name="q"], .search__input input') as HTMLInputElement;
    if (searchInput?.value?.trim()) {
      searchTerm = searchInput.value.trim();
    }
    // Method 2: Look for search term in URL if on search results page
    if (!searchTerm && window.location.pathname.includes('/search')) {
      const urlParams = new URLSearchParams(window.location.search);
      searchTerm = urlParams.get('q') || '';
    }
    // Method 3: Look for search term in data attributes
    if (!searchTerm) {
      const searchElement = document.querySelector('[data-search-term]');
      searchTerm = searchElement?.getAttribute('data-search-term') || '';
    }
    if (searchTerm) {
      return `autocomplete-${searchTerm}`;
    }
  }
  return null;
};

// Helper function to handle notifications
const handleNotification = async (handle: string, payload: string, action: 'click' | 'addToCart') => {
  const hasConsent = hasCookieConsent();
  if (DEBUG) {
    console.info(`${action} ${handle}:${payload} (Consent: ${hasConsent})`);
    return;
  }
  elevateFetchShim();
  try {
    const api = getApi();
    await api.notify[action](payload);
  } catch (error) {
    console.error(`Failed to track ${action} for ${handle}:`, error);
  }
};

// Dispatch the event correctly to avoid "already being dispatched" error
const redispatchEvent = (event: MouseEvent, target: EventTarget): void => {
  try {
    // Reset the click tracker globally BEFORE dispatching
    if ((window as any).blubolt?.Voyado) {
      (window as any).blubolt.Voyado._clickInProgress = false;
    }

    // Create a NEW event based on the original one
    const newEvent = new MouseEvent(event.type, {
      bubbles: event.bubbles,
      cancelable: event.cancelable,
      view: event.view,
      detail: event.detail,
      screenX: event.screenX,
      screenY: event.screenY,
      clientX: event.clientX,
      clientY: event.clientY,
      ctrlKey: event.ctrlKey,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      button: event.button,
      buttons: event.buttons,
      relatedTarget: event.relatedTarget,
    });

    // Tag the event so we don't track it again
    (newEvent as any)._id = 'voyado-tracked';

    target.dispatchEvent(newEvent);
  } catch (e) {
    console.error('Failed to redispatch event:', e);
    if ((window as any).blubolt?.Voyado) {
      (window as any).blubolt.Voyado._clickInProgress = false;
    }
  }
};

// Tracking click
export const trackClick = (): void => {
  if ((window as any).blubolt?.Voyado?._clickTracked) {
    return;
  }
  if (!(window as any).blubolt) (window as any).blubolt = {};
  if (!(window as any).blubolt.Voyado) (window as any).blubolt.Voyado = {};
  (window as any).blubolt.Voyado._clickTracked = true;

  document.addEventListener('click', async (e: MouseEvent) => {
    // 1. Don't track if this is our own re-dispatched event
    if ((e as any)._id === 'voyado-tracked') return;

    // 2. Hard Line: Don't track behavioural clicks if consent is missing
    if (!hasCookieConsent()) return;

    // 3. Don't track if a click is already being processed (global lock)
    if ((window as any).blubolt.Voyado._clickInProgress) return;

    const target = e.target as HTMLElement;
    const a = target.closest('a');

    // Early return conditions
    if (target.closest('.product-card-swiper-button')) {
      return;
    }

    if (a?.classList.contains('search__tab-menu-link')) {
      return;
    }

    const trackedPaths = ['article', 'collections', 'page', 'products', 'blogs'];
    const isTracked = a?.href && trackedPaths.some(path => a?.href.includes(path));

    if (!isTracked) {
      return;
    }

    let payload, eventType;

    if (target.closest('[data-wishlist]') && (window as any).blubolt.isLoggedIn) {
      const wishlistEl = target.closest('[data-wishlist]');
      payload = wishlistEl.getAttribute('data-prodid');
      eventType = wishlistEl.getAttribute('data-added') === 'true' ? 'removeFavorite' : 'addFavorite';
    } else {
      const ticketEl = target.closest('[data-ticket]') as HTMLElement;
      let resourceId: string;

      if (target.closest('[data-voyado-recommendations]')) {
        const recommendationsContainer = target.closest('[data-voyado-recommendations]') as HTMLElement;
        const recommendationType = recommendationsContainer?.dataset.recommendationType;
        const pageMeta = (window as any).ShopifyAnalytics?.meta?.page;
        const isCartPage = window.location.pathname === '/cart' || pageMeta?.pageType === 'cart';

        if (isCartPage && !pageMeta?.resourceId) {
          resourceId = 'recommendations-cart';
        } else {
          const baseResourceId = `recommendations-${pageMeta?.resourceId || 'undefined'}`;
          resourceId = recommendationType ? `${baseResourceId}-${recommendationType.toLowerCase()}` : baseResourceId;
        }
      } else {
        const autocompleteResourceId = getAutocompleteResourceId(target);
        if (autocompleteResourceId) {
          resourceId = autocompleteResourceId;
        } else {
          const isContentUrl = a.href && (a.href.includes('/blogs/') || a.href.includes('/pages/') || a.href.includes('/collections/'));
          if (isContentUrl && ticketEl?.dataset.ticket) {
            const storedTickets = JSON.parse(localStorage.getItem('bb-voyado-tickets') || '{}');
            const currentSession = getVoyadoSession(localStorageBackedSession);
            const sessionKeys = currentSession();
            const customerKey = sessionKeys.customerKey;
            const sessionKey = sessionKeys.sessionKey;

            let autocompleteKeys: string[] = [];
            if (customerKey && sessionKey && storedTickets[customerKey]?.[sessionKey]) {
              autocompleteKeys = Object.keys(storedTickets[customerKey][sessionKey]).filter(key => key.startsWith('autocomplete-'));
            }

            if (autocompleteKeys.length > 0) {
              resourceId = autocompleteKeys[0];
            } else {
              resourceId = getCurrentResourceId();
            }
          } else {
            resourceId = getCurrentResourceId();
          }
        }
      }

      const ticket = getTicket(handleFromUrl(a.href), resourceId) || ticketEl?.dataset.ticket;
      if (!ticket) return;

      payload = ticket;
      eventType = 'click';
    }

    // Set the global lock and stop event
    (window as any).blubolt.Voyado._clickInProgress = true;
    e.stopPropagation();
    e.preventDefault();

    await handleNotification(handleFromUrl(a.href), payload, eventType as any)
      .finally(() => {
        redispatchEvent(e, target);
      });
  }, { capture: true, passive: false });
};

export const trackAdd = (): void => {
  if ((window as any).blubolt?.Voyado?._addTracked) {
    return;
  }
  if (!(window as any).blubolt) (window as any).blubolt = {};
  if (!(window as any).blubolt.Voyado) (window as any).blubolt.Voyado = {};
  (window as any).blubolt.Voyado._addTracked = true;

  const handleAddToCart = async (e: CustomEvent<{ itemAdded: { product_id: number; handle: string } }>) => {
    // Hard Line: Don't track behavioural add-to-cart if consent is missing
    if (!hasCookieConsent()) return;

    if (!e.detail?.itemAdded) return;
    const { product_id, handle } = e.detail.itemAdded;
    const productId = product_id.toString();
    const productHandle = handle;

    const resourceIds = [getCurrentResourceId()];
    const pageMeta = (window as any).ShopifyAnalytics?.meta?.page;
    if (pageMeta) {
      const isCartPage = window.location.pathname === '/cart' || pageMeta.pageType === 'cart';
      let recommendationsResourceId: string;
      if (isCartPage && !pageMeta.resourceId) {
        recommendationsResourceId = 'recommendations-cart';
      } else {
        recommendationsResourceId = `recommendations-${pageMeta.resourceId || 'undefined'}`;
      }
      if (!resourceIds.includes(recommendationsResourceId)) {
        resourceIds.push(recommendationsResourceId);
      }
    }

    let ticket: string | undefined;
    for (const resourceId of resourceIds) {
      ticket = getTicket(productHandle, resourceId);
      if (ticket) break;
    }

    if (!ticket) {
      try {
        await setTicket(productId);
        const currentResourceId = getCurrentResourceId();
        ticket = getTicket(productHandle, currentResourceId);
        if (!ticket) {
          for (const resourceId of resourceIds) {
            ticket = getTicket(productHandle, resourceId);
            if (ticket) break;
          }
        }
      } catch (error) {
        console.error('Error generating ticket for add-to-cart:', error);
      }
    }

    if (ticket) {
      await handleNotification(productId, ticket, 'addToCart');
    }
  };

  window.addEventListener('blubolt:add-to-cart', handleAddToCart as any);
};

export const trackPayment = async (lineItems: any[]): Promise<void> => {
  if (!hasCookieConsent() || !lineItems || lineItems.length === 0) return;
  elevateFetchShim();
  try {
    const api = getApi();
    const payment = (api.notify as any).payment();
    lineItems.forEach((item) => {
      payment.add({
        quantity: item.quantity,
        unitSellingPrice: item.unitSellingPrice,
        productKey: item.productKey,
        variantKey: item.variantKey
      });
    });
    await payment.send();
  } catch (error) {
    console.error('Failed to track payment notification:', error);
  }
};